/**
 * Parser de comandos em linguagem natural para criação/edição de tarefas.
 * 
 * Exemplos:
 * - "Criar tarefa: Desenvolver landing page com deadline 15/05"
 * - "Editar tarefa de design para passar para revisão"
 * - "/create-task Implementar autenticação"
 * - "Agendar reunião com design team"
 */

export interface ParsedCommand {
  intent: "create_task" | "update_task" | "assign_task" | "none";
  rawText: string;
  parameters: {
    title?: string;
    description?: string;
    teamId?: string;
    taskId?: string;
    deadline?: string;
    priority?: "low" | "medium" | "high";
    assignee?: string;
  };
  confidence: number; // 0-1
  suggestion?: string; // Sugestão amigável do que será feito
}

const INTENT_KEYWORDS = {
  create_task: [
    /criar\s+tarefa/i,
    /nova\s+tarefa/i,
    /adicionar\s+tarefa/i,
    /registrar\s+tarefa/i,
    /create\s+task/i,
    /new\s+task/i,
    /^\/create-task/i,
  ],
  update_task: [
    /editar\s+tarefa/i,
    /atualizar\s+tarefa/i,
    /modificar\s+tarefa/i,
    /update\s+task/i,
    /edit\s+task/i,
    /^\/update-task/i,
  ],
  assign_task: [
    /designar\s+tarefa/i,
    /delegar\s+tarefa/i,
    /assign\s+task/i,
    /delegate\s+task/i,
  ],
};

const PRIORITY_KEYWORDS = {
  high: /\b(urgente|crítica|alta prioridade|high|priority)\b/i,
  medium: /\b(normal|média|medium)\b/i,
  low: /\b(baixa|low)\b/i,
};

const DEADLINE_PATTERNS = [
  /(?:até|deadline|prazo|até\s+|para)\s+(\d{1,2}\/\d{1,2}(?:\/\d{4})?)/i,
  /(?:em)\s+(\d+)\s+(?:dia|dias)/i,
  /(?:semana)\s+(?:que vem|próxima)/i,
];

/**
 * Detecta a intenção do comando a partir do texto
 */
export function detectIntent(text: string): ParsedCommand["intent"] {
  for (const [intent, patterns] of Object.entries(INTENT_KEYWORDS)) {
    if (patterns.some((pattern) => pattern.test(text))) {
      return intent as ParsedCommand["intent"];
    }
  }
  return "none";
}

/**
 * Extrai prioridade do texto
 */
function extractPriority(text: string): ParsedCommand["parameters"]["priority"] | undefined {
  for (const [priority, pattern] of Object.entries(PRIORITY_KEYWORDS)) {
    if (pattern.test(text)) {
      return priority as any;
    }
  }
  return undefined;
}

/**
 * Extrai data de deadline do texto
 */
function extractDeadline(text: string): string | undefined {
  for (const pattern of DEADLINE_PATTERNS) {
    const match = text.match(pattern);
    if (match?.[1]) {
      return match[1];
    }
  }
  return undefined;
}

/**
 * Limpa o texto de prefixos de comando
 */
function cleanCommandText(text: string): string {
  return text
    .replace(/^\/create-task\s+/i, "")
    .replace(/^\/update-task\s+/i, "")
    .replace(/^criar\s+tarefa\s*:\s*/i, "")
    .replace(/^editar\s+tarefa\s*:\s*/i, "")
    .trim();
}

/**
 * Parser principal que analisa entrada em linguagem natural
 */
export function parseCommand(text: string): ParsedCommand {
  const intent = detectIntent(text);
  const cleanedText = cleanCommandText(text);

  if (intent === "none") {
    return {
      intent: "none",
      rawText: text,
      parameters: {},
      confidence: 0,
    };
  }

  const parameters: ParsedCommand["parameters"] = {
    title: extractTitle(cleanedText),
    description: cleanedText,
    priority: extractPriority(cleanedText),
    deadline: extractDeadline(cleanedText),
  };

  // Gerar sugestão
  const suggestion = generateSuggestion(intent, parameters);

  return {
    intent,
    rawText: text,
    parameters,
    confidence: 0.7, // Confiança padrão para entrada natural
    suggestion,
  };
}

/**
 * Extrai o título (primeira frase/linha)
 */
function extractTitle(text: string): string | undefined {
  // Pega até primeira quebra de linha ou ponto, limitado a 80 chars
  const match = text.match(/^([^.\n]+)/);
  if (match?.[1]) {
    const title = match[1].trim();
    // Remove palavras-chave de tempo/deadline
    return title
      .replace(/\s+(até|deadline|prazo|para|em)\s+.*/i, "")
      .trim()
      .substring(0, 80);
  }
  return undefined;
}

/**
 * Gera uma sugestão amigável da ação que será executada
 */
function generateSuggestion(intent: ParsedCommand["intent"], params: ParsedCommand["parameters"]): string {
  const title = params.title || "nova tarefa";
  const priority = params.priority ? ` com prioridade ${params.priority}` : "";
  const deadline = params.deadline ? ` até ${params.deadline}` : "";

  switch (intent) {
    case "create_task":
      return `Criar tarefa: "${title}"${priority}${deadline}`;
    case "update_task":
      return `Editar tarefa: "${title}"${priority}${deadline}`;
    case "assign_task":
      return `Designar tarefa: "${title}" para a equipe${priority}`;
    default:
      return "Processar comando";
  }
}

/**
 * Valida se o comando extraído tem informações suficientes
 */
export function isValidCommand(parsed: ParsedCommand): boolean {
  if (parsed.intent === "none") return false;

  const hasTitle = !!(parsed.parameters.title && parsed.parameters.title.length >= 3);
  const hasDescription = !!(parsed.parameters.description && parsed.parameters.description.length >= 5);

  return hasTitle && hasDescription;
}

/**
 * Monta o texto final para enviar ao assistente com sugestão integrada
 */
export function buildCommandPrompt(parsed: ParsedCommand): string {
  if (!isValidCommand(parsed)) {
    return parsed.rawText; // Repassa texto original se não foi bem parsed
  }

  const lines = [parsed.suggestion || "", "", parsed.parameters.description || ""].filter(Boolean);
  return lines.join("\n");
}
